import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx'
import ExcelJS from 'exceljs'
import type { CheckInOutRecord } from '../types/checkinout'
import { CheckInOutStatus } from '../types/checkinout'

// ฟังก์ชันสำหรับจัดรูปแบบวันที่
export const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

// ฟังก์ชันสำหรับจัดรูปแบบเวลา
export const formatTime = (dateString: string | undefined) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ฟังก์ชันสำหรับจัดรูปแบบวันที่พร้อมเวลา
export const formatDateWithTime = (dateString: string | undefined) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ฟังก์ชันสำหรับสร้างช่วงวันที่
export const generateDateRange = (start: string, end: string): string[] => {
  const dates: string[] = []
  let current = new Date(start)
  const last = new Date(end)
  while (current <= last) {
    dates.push(formatDate(current.toISOString().split('T')[0]))
    current.setDate(current.getDate() + 1)
  }
  return dates
}

// ฟังก์ชันสำหรับ export แบบพื้นฐาน
export const handleExport = async (data: CheckInOutRecord[]) => {
  const sortingCheckinByUsernameActiveDate = data.sort((a, b) => {
    if (a.checkin.user?.[0]?.name === b.checkin.user?.[0]?.name) {
      return (
        new Date(a.checkin.activeTime || a.activeDate).getTime() -
        new Date(b.checkin.activeTime || b.activeDate).getTime()
      )
    }
    return a.checkin.user?.[0]?.name.localeCompare(b.checkin.user?.[0]?.name)
  })

  const exportData = sortingCheckinByUsernameActiveDate.map((record) => {
    const baseData: Record<string, string | number> = {
      ลำดับ: data.indexOf(record) + 1,
      วันที่: formatDate(record.activeDate),
      ชื่อพนักงาน: record.checkin.user?.[0]?.name || record.username,
      โครงการเข้างาน: record.checkin.project.name,
      เวลาเข้างาน: record.checkin.activeTime
        ? formatDateWithTime(record.checkin.activeTime)
        : '-',
    }

    if (record.checkout.length > 0) {
      baseData['โครงการออกงาน 1'] = record.checkout[0].project.name
      baseData['เวลาออกงาน 1'] = formatDateWithTime(record.checkout[0].activeTime)
    } else {
      baseData['โครงการออกงาน 1'] = '-'
      baseData['เวลาออกงาน 1'] = '-'
    }

    if (record.checkout.length > 1) {
      baseData['โครงการออกงาน 2'] = record.checkout[1].project.name
      baseData['เวลาออกงาน 2'] = formatDateWithTime(
        record.checkout[record.checkout.length - 1].activeTime,
      )
    } else {
      baseData['โครงการออกงาน 2'] = '-'
      baseData['เวลาออกงาน 2'] = '-'
    }

    return baseData
  })

  const ws = xlsxUtils.json_to_sheet(exportData)
  const wb = xlsxUtils.book_new()
  xlsxUtils.book_append_sheet(wb, ws, 'รายงานเข้า-ออกงาน')
  xlsxWriteFile(
    wb,
    `รายงานเข้า-ออกงาน_${new Date().toISOString().split('T')[0]}.xlsx`,
  )
}

// ฟังก์ชันสำหรับ export แบบที่ 2
export const handleExport2 = async (data: CheckInOutRecord[]) => {
  const sortingCheckinByUsernameActiveDate = data.sort((a, b) => {
    if (a.checkin.user?.[0]?.name === b.checkin.user?.[0]?.name) {
      return (
        new Date(a.checkin.activeTime || a.activeDate).getTime() -
        new Date(b.checkin.activeTime || b.activeDate).getTime()
      )
    }
    return a.checkin.user?.[0]?.name.localeCompare(b.checkin.user?.[0]?.name)
  })

  const exportData = sortingCheckinByUsernameActiveDate.map((record) => {
    const baseData: Record<string, string | number> = {
      ลำดับ: data.indexOf(record) + 1,
      วันที่: formatDate(record.activeDate),
      ชื่อพนักงาน: record.checkin.user?.[0]?.name || record.username,
      โครงการเข้างาน: record.checkin.project.name,
      เวลาเข้างาน: record.checkin.activeTime
        ? formatDateWithTime(record.checkin.activeTime)
        : '-',
    }

    if (record.checkout.length > 0) {
      const lastCheckout = record.checkout[record.checkout.length - 1]
      baseData['โครงการออกงาน'] = lastCheckout.project.name
      baseData['เวลาออกงาน'] = formatDateWithTime(lastCheckout.activeTime)
    } else {
      baseData['โครงการออกงาน'] = '-'
      baseData['เวลาออกงาน'] = '-'
    }

    return baseData
  })

  const ws = xlsxUtils.json_to_sheet(exportData)
  ws['!cols'] = [
    { wch: 6 }, // ลำดับ
    { wch: 12 }, // วันที่
    { wch: 35 }, // ชื่อพนักงาน
    { wch: 50 }, // โครงการเข้างาน
    { wch: 18 }, // เวลาเข้างาน
    { wch: 50 }, // โครงการออกงาน
    { wch: 18 }, // เวลาออกงาน
  ]

  const wb = xlsxUtils.book_new()
  xlsxUtils.book_append_sheet(wb, ws, 'รายงานเข้า-ออกงาน')
  xlsxWriteFile(
    wb,
    `รายงานเข้า-ออกงาน_${new Date().toISOString().split('T')[0]}.xlsx`,
  )
}

// ฟังก์ชันสำหรับ export แบบ ExcelJS
export const handleExportExcelJS = async (data: CheckInOutRecord[]) => {
  const sortingCheckinByUsernameActiveDate = data.sort((a, b) => {
    if (a.checkin.user?.[0]?.name === b.checkin.user?.[0]?.name) {
      return (
        new Date(a.checkin.activeTime || a.activeDate).getTime() -
        new Date(b.checkin.activeTime || b.activeDate).getTime()
      )
    }
    return a.checkin.user?.[0]?.name.localeCompare(b.checkin.user?.[0]?.name)
  })

  const exportData = sortingCheckinByUsernameActiveDate.map((record, idx) => {
    const baseData: Record<string, string | number> = {
      ลำดับ: idx + 1,
      รหัสพนักงาน: record.checkin.user?.[0]?.employeeCode || '-',
      ชื่อพนักงาน: record.checkin.user?.[0]?.name || record.username,
      วันที่: formatDate(record.activeDate),
      โครงการเข้างาน: record.checkin.project.name,
      เวลาเข้างาน: record.checkin.activeTime
        ? formatDateWithTime(record.checkin.activeTime)
        : '-',
    }
    if (record.checkout.length > 0) {
      const lastCheckout = record.checkout[record.checkout.length - 1]
      baseData['โครงการออกงาน'] = lastCheckout.project.name
      baseData['เวลาออกงาน'] = formatDateWithTime(lastCheckout.activeTime)
    } else {
      baseData['โครงการออกงาน'] = '-'
      baseData['เวลาออกงาน'] = '-'
    }
    return baseData
  })

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('รายงานเข้า-ออกงาน')

  const formatDateTH = (dateString: string) => {
    if (!dateString) return '-'
    const d = new Date(dateString)
    return d.toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const headers = [
    'ลำดับ',
    'รหัสพนักงาน',
    'ชื่อพนักงาน',
    'โครงการเข้างาน',
    'เวลาเข้างาน',
    'โครงการออกงาน',
    'เวลาออกงาน',
  ]

  worksheet.addRow(headers)

  exportData.forEach((row) => {
    worksheet.addRow(headers.map((h) => row[h]))
  })

  const thickBorderRows: number[] = []
  let prevName = ''
  for (let i = 0; i < exportData.length; i++) {
    const currentName = String(exportData[i]['ชื่อพนักงาน'])
    if (i === 0 || currentName !== prevName) {
      thickBorderRows.push(i + 2)
    }
    prevName = currentName
  }

  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, size: 12, color: { argb: 'FF000000' } }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF70A8E0' },
    }
    cell.alignment = { vertical: 'middle', horizontal: 'center' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })

  const colCount = headers.length
  for (const rowIdx of thickBorderRows) {
    const excelRow = worksheet.getRow(rowIdx)
    for (let colIdx = 1; colIdx <= colCount; colIdx++) {
      const cell = excelRow.getCell(colIdx)
      cell.border = {
        ...cell.border,
        top: { style: 'thick' },
      }
    }
  }

  const colWidths = [6, 12, 30, 50, 18, 50, 18]
  colWidths.forEach((w, idx) => {
    worksheet.getColumn(idx + 1).width = w
  })

  const colCountForBorder = headers.length
  for (let rowIdx = 1; rowIdx <= exportData.length + 1; rowIdx++) {
    worksheet.getRow(rowIdx).height = 20
    worksheet.getRow(rowIdx).font = { size: 12 }
    worksheet.getRow(rowIdx).alignment = {
      vertical: 'middle',
      horizontal: 'left',
    }
    for (let colIdx = 1; colIdx <= colCountForBorder; colIdx++) {
      const cell = worksheet.getRow(rowIdx).getCell(colIdx)
      if (rowIdx === 1 && colIdx === 1) {
        cell.border = {
          ...cell.border,
          top: { style: 'thick' },
          left: { style: 'thick' },
        }
      } else if (rowIdx === 1 && colIdx === colCountForBorder) {
        cell.border = {
          ...cell.border,
          top: { style: 'thick' },
          right: { style: 'thick' },
        }
      } else if (rowIdx === exportData.length + 1 && colIdx === 1) {
        cell.border = {
          ...cell.border,
          bottom: { style: 'thick' },
          left: { style: 'thick' },
        }
      } else if (rowIdx === exportData.length + 1 && colIdx === colCountForBorder) {
        cell.border = {
          ...cell.border,
          bottom: { style: 'thick' },
          right: { style: 'thick' },
        }
      } else if (rowIdx === 1) {
        cell.border = { ...cell.border, top: { style: 'thick' } }
      } else if (rowIdx === exportData.length + 1) {
        cell.border = { ...cell.border, bottom: { style: 'thick' } }
      } else if (colIdx === 1) {
        cell.border = { ...cell.border, left: { style: 'thick' } }
      } else if (colIdx === colCountForBorder) {
        cell.border = { ...cell.border, right: { style: 'thick' } }
      }
    }
  }

  for (let rowIdx = 2; rowIdx <= exportData.length + 1; rowIdx++) {
    worksheet.getRow(rowIdx).getCell(1).alignment = {
      vertical: 'middle',
      horizontal: 'right',
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `รายงานเข้า-ออกงาน_${new Date().toISOString().split('T')[0]}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

// ฟังก์ชันสำหรับ export แบบ ExcelJS ทั้งวัน
export const handleAllDayExportExcelJS = async (
  data: CheckInOutRecord[],
  startDate: string,
  endDate: string,
) => {
  const sortingCheckinByUsernameActiveDate = data.sort((a, b) => {
    if (a.checkin.user?.[0]?.name === b.checkin.user?.[0]?.name) {
      return (
        new Date(formatDate(a.checkin.activeTime)).getTime() -
        new Date(formatDate(b.checkin.activeTime)).getTime()
      )
    }
    return a.checkin.user?.[0]?.name.localeCompare(b.checkin.user?.[0]?.name)
  })

  sortingCheckinByUsernameActiveDate.forEach((record) => {
    record.activeDate = record.checkin.activeTime
      ? formatDate(record.checkin.activeTime)
      : record.activeDate
    record.checkin.activeTime = record.checkin.activeTime
      ? formatDateWithTime(record.checkin.activeTime)
      : record.checkin.activeTime
    // console.log(`${record.activeDate} ---- ${record.checkin.activeTime}`)
  })

  const allDates = generateDateRange(startDate, endDate)
//   allDates.forEach((date) => {
//     console.log(date)
//   })

  const groupedByUser = new Map<string, CheckInOutRecord[]>()
  sortingCheckinByUsernameActiveDate.forEach((record) => {
    const key = record.username
    if (!groupedByUser.has(key)) groupedByUser.set(key, [])
    groupedByUser.get(key)!.push(record)
  })

  const filledRecords: CheckInOutRecord[] = []

  for (const [username, records] of Array.from(groupedByUser.entries())) {
    const name = records[0]?.checkin?.user?.[0]?.name || username
    const employeeCode = records[0]?.checkin?.user?.[0]?.employeeCode || '-'

    const recordsByDate = new Map(
      records.map((r: CheckInOutRecord) => [
        `${r.activeDate}_${r.checkin.activeTime || ''}`,
        r,
      ]),
    )

    allDates.forEach((date: string) => {
      const hasRecordForDate = Array.from(recordsByDate.keys()).some((key) =>
        key.startsWith(date),
      )

      if (hasRecordForDate) {
        records
          .filter((r) => r.activeDate === date)
          .forEach((r) => filledRecords.push(r))
      } else {
        const emptyRecord: CheckInOutRecord = {
          username,
          activeDate: date,
          checkin: {
            _id: '',
            projectId: '',
            username,
            status: 'CHECKIN' as CheckInOutStatus,
            activeTime: '',
            location: { latitude: 0, longitude: 0, address: '' },
            images: [],
            note: '',
            isLateTime: false,
            isDistanceExceeded: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            user: [{ username, name, employeeCode }],
            project: {
              _id: '',
              code: '',
              name: '-',
              isActive: true,
              status: 'active',
              workStartTime: '09:00',
              workEndTime: '18:00',
              maxLateTime: '30',
              objectId: '',
            },
          },
          checkout: [],
        }
        filledRecords.push(emptyRecord)
      }
    })
  }

  const finalSorted = filledRecords.sort((a, b) => {
    const nameA = a.checkin.user?.[0]?.name || ''
    const nameB = b.checkin.user?.[0]?.name || ''

    if (nameA === nameB) {
      const dateA = new Date(a.activeDate)
      const dateB = new Date(b.activeDate)

      if (dateA.getTime() === dateB.getTime()) {
        const timeA = a.checkin.activeTime
          ? new Date(a.checkin.activeTime).getTime()
          : 0
        const timeB = b.checkin.activeTime
          ? new Date(b.checkin.activeTime).getTime()
          : 0
        return timeA - timeB
      }
      return dateA.getTime() - dateB.getTime()
    }
    return nameA.localeCompare(nameB)
  })

  const exportData = finalSorted.map((record, idx) => {
    const baseData: Record<string, string | number> = {
      ลำดับ: idx + 1,
      รหัสพนักงาน: record.checkin.user?.[0]?.employeeCode || '-',
      ชื่อพนักงาน: record.checkin.user?.[0]?.name || record.username,
      โครงการเข้างาน: record.checkin.project?.name || '-',
      วันที่: record.activeDate,
      เวลาเข้างาน: record.checkin.activeTime
        ? record.checkin.activeTime
        : '-',
    }

    if (record.checkout?.length > 0) {
      const lastCheckout = record.checkout[record.checkout.length - 1]
      baseData['โครงการออกงาน'] = lastCheckout.project?.name || '-'
      baseData['เวลาออกงาน'] = formatDateWithTime(lastCheckout.activeTime)
    } else {
      baseData['โครงการออกงาน'] = '-'
      baseData['เวลาออกงาน'] = '-'
    }

    return baseData
  })

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('รายงานเข้า-ออกงาน')

  const formatDateTH = (dateString: string) => {
    if (!dateString) return '-'
    const d = new Date(dateString)
    return d.toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const dateRangeText = `ข้อมูลตั้งแต่ วันที่ ${formatDateTH(
    startDate,
  )} - ${formatDateTH(endDate)}`
  worksheet.addRow([dateRangeText])
  worksheet.mergeCells('A1:H1')
  worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'left' }

  const headers = [
    'ลำดับ',
    'รหัสพนักงาน',
    'ชื่อพนักงาน',
    'โครงการเข้างาน',
    'วันที่',
    'เวลาเข้างาน',
    'โครงการออกงาน',
    'เวลาออกงาน',
  ]
  worksheet.addRow(headers)

  exportData.forEach((row) => {
    worksheet.addRow(headers.map((h) => row[h]))
  })

  const thickBorderRows: number[] = []
  let prevName = ''
  for (let i = 0; i < exportData.length; i++) {
    const currentName = String(exportData[i]['ชื่อพนักงาน'])
    if (i === 0 || currentName !== prevName) {
      thickBorderRows.push(i + 3)
    }
    prevName = currentName
  }

  worksheet.getRow(2).eachCell((cell) => {
    cell.font = { bold: true, size: 12, color: { argb: 'FF000000' } }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF70A8E0' },
    }
    cell.alignment = { vertical: 'middle', horizontal: 'center' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })

  const colCount = headers.length
  for (const rowIdx of thickBorderRows) {
    const excelRow = worksheet.getRow(rowIdx)
    for (let colIdx = 1; colIdx <= colCount; colIdx++) {
      const cell = excelRow.getCell(colIdx)
      cell.border = {
        ...cell.border,
        top: { style: 'thick' },
      }
    }
  }

  const colWidths = [6, 12, 30, 50, 12, 18, 50, 18]
  colWidths.forEach((w, idx) => {
    worksheet.getColumn(idx + 1).width = w
  })

  const colCountForBorder = headers.length
  for (let rowIdx = 2; rowIdx <= exportData.length + 2; rowIdx++) {
    worksheet.getRow(rowIdx).height = 20
    worksheet.getRow(rowIdx).font = { size: 12 }
    worksheet.getRow(rowIdx).alignment = {
      vertical: 'middle',
      horizontal: 'left',
    }
    for (let colIdx = 1; colIdx <= colCountForBorder; colIdx++) {
      const cell = worksheet.getRow(rowIdx).getCell(colIdx)
      if (rowIdx === 2 && colIdx === 1) {
        cell.border = {
          ...cell.border,
          top: { style: 'thick' },
          left: { style: 'thick' },
        }
      } else if (rowIdx === 2 && colIdx === colCountForBorder) {
        cell.border = {
          ...cell.border,
          top: { style: 'thick' },
          right: { style: 'thick' },
        }
      } else if (rowIdx === exportData.length + 2 && colIdx === 1) {
        cell.border = {
          ...cell.border,
          bottom: { style: 'thick' },
          left: { style: 'thick' },
        }
      } else if (rowIdx === exportData.length + 2 && colIdx === colCountForBorder) {
        cell.border = {
          ...cell.border,
          bottom: { style: 'thick' },
          right: { style: 'thick' },
        }
      } else if (rowIdx === 2) {
        cell.border = { ...cell.border, top: { style: 'thick' } }
      } else if (rowIdx === exportData.length + 2) {
        cell.border = { ...cell.border, bottom: { style: 'thick' } }
      } else if (colIdx === 1) {
        cell.border = { ...cell.border, left: { style: 'thick' } }
      } else if (colIdx === colCountForBorder) {
        cell.border = { ...cell.border, right: { style: 'thick' } }
      }
    }
  }

  for (let rowIdx = 3; rowIdx <= exportData.length + 2; rowIdx++) {
    worksheet.getRow(rowIdx).getCell(1).alignment = {
      vertical: 'middle',
      horizontal: 'right',
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `รายงานเข้า-ออกงาน_${new Date().toISOString().split('T')[0]}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
} 